import type { TestCase, TestEnvironment, TestLink, TestPriority, TestResult } from "../api/types";

/* Testing helpers (docs/test-suite.md section 8). */

/** Results a case can be run from: a new link, or one whose fix is ready to check. */
export const READY_RESULTS: TestResult[] = ["not_run", "retest"];

/** Results the portal's web UI no longer offers as a choice. They stay available through the API
    and MCP, and existing tests with them still show their chip and label. */
export const HIDDEN_RESULTS: TestResult[] = ["retest", "untestable", "waiting", "manual"];

/** Results a tester can record (Not run comes from the system). */
export const RECORDABLE: TestResult[] = ["pass", "fail", "blocked", "functionality_changed", "waived", "na"];

/** Results that need a note: the run is refused without one (section 2.5). */
export const NOTE_REQUIRED: TestResult[] = ["fail", "blocked", "manual", "functionality_changed", "waived"];

export const PRIORITY_LABEL: Record<TestPriority, string> = { high: "High", medium: "Medium", low: "Low" };
const PRIORITY_ORDER: Record<TestPriority, number> = { high: 0, medium: 1, low: 2 };

export const DEVICE_LABEL: Record<TestEnvironment["device"], string> = {
  desktop: "Desktop",
  tablet: "Tablet",
  mobile: "Mobile",
  tv: "TV",
};

export interface Assigned {
  test: TestCase;
  link: TestLink;
}

/** The case–environment pairs assigned to this user that are ready to run, Retest first, then by
    priority. */
export function readyAssigned(cases: TestCase[], userId: string, environmentId?: string): Assigned[] {
  const out: Assigned[] = [];
  for (const test of cases) {
    if (test.state !== "active") continue;
    for (const link of test.links) {
      if (link.assignee?.id !== userId) continue;
      if (!READY_RESULTS.includes(link.current_result)) continue;
      if (environmentId && link.environment.id !== environmentId) continue;
      out.push({ test, link });
    }
  }
  return out.sort(
    (a, b) =>
      Number(b.link.current_result === "retest") - Number(a.link.current_result === "retest") ||
      PRIORITY_ORDER[a.test.priority] - PRIORITY_ORDER[b.test.priority] ||
      a.test.code.localeCompare(b.test.code),
  );
}

/** Ready assigned tests per environment where Start is offered: the environment is Ready (a Completed one has nothing left to test). */
export function startable(
  ready: Assigned[],
  environments: TestEnvironment[],
): { environment: TestEnvironment; count: number }[] {
  const out: { environment: TestEnvironment; count: number }[] = [];
  for (const environment of environments) {
    if (environment.status !== "ready") continue;
    const count = ready.filter((r) => r.link.environment.id === environment.id).length;
    if (count) out.push({ environment, count });
  }
  return out;
}
